/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { Subject } from 'rxjs';

import { DefaultActionTypes } from './DefaultActionTypes';
import { hasId } from './interfaces';

import { iterateStuff } from 'utils/model-utils';
import { ListLike } from 'src/app/app-data/interfaces/list-like';
import { FancyAction } from '../base/FancyAction';
import { FancyFirebaseList } from '../base/FancyFirebaseList';
import { FancyFirebaseObject } from '../base/FancyFirebaseObject';

/**
 * An abstract class to be extended by "a thing that interfaces with firebase".
 * It's intended to wrap the FancyFirebaseList as the "write" component to correspond with it's reads.
 * A default set of actions is included for standard firebase manipulation.
 * PlanState(or projectState?) is not included by default, but the extending class can opt into it
 * if it needs to know about the current state of the app. Just pass it in as a second argument of
 * the constructor. In general all the default methods operate on the Model or List<Model> system where possible.
 * @param list [description]
 */
export abstract class FancyActionableFirebase<
	FancyModel extends hasId,
	FancyCustomAction extends FancyAction,
	ListOrObject extends FancyFirebaseList<FancyModel, FancyCustomAction> | FancyFirebaseObject<FancyModel>,
	FancyCustomActionType extends string = DefaultActionTypes
> {
	private shutdownSubject = new Subject<void>();
	/** list of functions to be called on destruction */
	private cleanupList: Array<() => void> = [];

	constructor(public data: ListOrObject) {}

	protected pushAction(
		type: DefaultActionTypes | FancyCustomActionType,
		key: string,
		payload: any,
		additionalArgs?: object
	) {
		//this coercion should be safe because the type checking will all be done via the interfaces
		const action = new FancyAction(payload, type, key) as FancyCustomAction;
		if (additionalArgs) {
			Object.assign(action, additionalArgs);
		}
		this.data.appEvent$.next(action);
	}

	//don't see any logical reason to try and make this support lists, since the
	//list would need to be in a specific format or we'd need a getter to keep it generic
	//so... might as well just iterate externally
	/** applies data to the current view, can only operate on one item at a time */
	public localUpdate(key: string, payload: object, renderType?: string) {
		const additionalArgs = { renderType };
		this.pushAction(DefaultActionTypes.localUpdate, key, payload, additionalArgs);
	}

	/** switches to a different renderType (view?) */
	public changeView(list: ListLike<FancyModel> | FancyModel, renderType: string) {
		const additionalArgs = { renderType };
		iterateStuff(list, (item) => {
			this.pushAction(DefaultActionTypes.changeView, item.$id, item, additionalArgs);
		});
	}
	/**
	 * switches to a different renderType. Currently functionaly equivalent changeView, the alternative action allows
	 * for end user code to handle the two actions differently if so desired
	 */
	public abortView(list: ListLike<FancyModel> | FancyModel, renderType: string) {
		const additionalArgs = { renderType };
		iterateStuff(list, (item) => {
			this.pushAction(DefaultActionTypes.abortView, item.$id, item, additionalArgs);
		});
	}

	/** mark a selected item as having loaded it's additional data */
	public setSelectionDataLoaded(list: ListLike<FancyModel> | FancyModel, loaded = true) {
		const additionalArgs = { loaded };
		iterateStuff(list, (item) => {
			this.pushAction(DefaultActionTypes.selectionDataLoaded, item.$id, item, additionalArgs);
		});
	}

	/** saves the current set of local data to firebase */
	public persistData(list: ListLike<FancyModel> | FancyModel, renderType?: string) {
		const additionalArgs = renderType ? { renderType } : undefined;
		iterateStuff(list, (item) => {
			this.pushAction(DefaultActionTypes.persistData, item.$id, item, additionalArgs);
		});
	}

	/**
	 * creates a new "model" and pushes it into the list
	 * @param initialData props to be created
	 * @param idOverride set if you want the "Thing" you're creating to have a specific id, otherwise one will be generated (preferred)
	 * @param persistImmediately set to true (default false) to persist the new "Thing" as soon as it's been created
	 */
	public addItem(initialData: object, idOverride?: string, persistImmediately = false) {
		const additionalArg = { idOverride, persistImmediately };
		this.pushAction(DefaultActionTypes.addItem, 'empty', initialData, additionalArg);
	}

	/**
	 * deprecated, just here for lazier compat with old stuff. Still adds locally before persisting, but then also persists
	 * ideal workflow will be: add locally, open up to ui for editing and localUpdates. Persist if success, remove if failure
	 */
	public addItemLegacy(initialData: object, idOverride?: string) {
		this.addItem(initialData, idOverride, true);
	}

	/** removes an item from the list, this persists */
	public removeItem(list: ListLike<FancyModel> | FancyModel, additionalArgs?: object) {
		iterateStuff(list, (item) => {
			this.pushAction(DefaultActionTypes.removeItem, item.$id, item, additionalArgs);
		});
	}

	/**
	 * Push any functions that want to invoke destruction calls when this class is destroyed.
	 * @param fns List of functions that want to be called
	 */
	public addDestroyer(...fns: Array<() => void>) {
		this.cleanupList.push(...fns);
	}

	public destroy() {
		this.data.destroy();
		this.cleanupList.forEach((fn) => fn());
		this.cleanupList = [];
		this.data = null;
	}
}
